import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, CreditCard } from 'lucide-react';
const AVATAR_IMAGES = ['https://i.pravatar.cc/100?img=10', 'https://i.pravatar.cc/100?img=11', 'https://i.pravatar.cc/100?img=12', 'https://i.pravatar.cc/100?img=5', 'https://i.pravatar.cc/100?img=13', 'https://i.pravatar.cc/100?img=14', 'https://i.pravatar.cc/100?img=15', 'https://i.pravatar.cc/100?img=16', 'https://i.pravatar.cc/100?img=17', 'https://i.pravatar.cc/100?img=18', 'https://i.pravatar.cc/100?img=19', 'https://i.pravatar.cc/100?img=20', 'https://i.pravatar.cc/100?img=21', 'https://i.pravatar.cc/100?img=22', 'https://i.pravatar.cc/100?img=23', 'https://i.pravatar.cc/100?img=24'];
export function Hero({
  onStartQuiz
}: {
  onStartQuiz: () => void;
}) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setOffset(prev => prev + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  const visibleAvatars = AVATAR_IMAGES.slice(0, 12);
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-navy pt-16 pb-12" data-id="element-50">
      {/* Organic Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-51">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-terracotta/15 blur-[100px] animate-float" data-id="element-52" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-yellow/15 blur-[120px] animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-53" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-54">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-id="element-55">
          {/* Left Content */}
          <div className="lg:col-span-7 flex flex-col items-start text-left" data-id="element-56">
            {/* Top label */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block bg-yellow/20 border border-yellow/30 backdrop-blur-sm px-5 py-2 rounded-full mb-8" data-id="element-57">
              <span className="text-yellow font-heading font-bold tracking-wide text-sm" data-id="element-58">
                🧠 Descubre tu nivel de ansiedad social
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-offwhite leading-[1.15] mb-6" data-id="element-59">
              ¿Por qué mi mente sigue entrando en{' '}
              <span className="text-yellow" data-id="element-60">'pánico'</span> cada vez que intento
              hablar con alguien... dejándome días analizando cada palabra que
              dije mal?
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-offwhite/80 mb-8 max-w-2xl font-medium" data-id="element-61">
              Un análisis de 2 minutos basado en neurobiología aplicada.
            </motion.p>

            {/* CTA Button — Red with heartbeat */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="w-full max-w-md" data-id="element-62">
              <button onClick={onStartQuiz} className="w-full bg-red-600 hover:bg-red-700 text-white font-heading font-bold text-lg py-5 px-8 rounded-full shadow-lg animate-heartbeat flex items-center justify-center" data-id="element-63">
                INICIAR MI ANÁLISIS GRATUITO →
              </button>

              {/* Trust micro-copy — more eye-catching */}
              <div className="flex items-center justify-center gap-4 mt-5 flex-wrap" data-id="element-64">
                <div className="flex items-center gap-1.5 bg-white/10 border border-white/15 rounded-full px-4 py-2" data-id="element-65">
                  <CreditCard className="w-4 h-4 text-yellow" data-id="element-66" />
                  <span className="text-sm font-bold text-offwhite" data-id="element-67">
                    Sin pagar
                  </span>
                </div>
                <div className="flex items-center gap-1.5 bg-white/10 border border-white/15 rounded-full px-4 py-2" data-id="element-68">
                  <Clock className="w-4 h-4 text-yellow" data-id="element-69" />
                  <span className="text-sm font-bold text-offwhite" data-id="element-70">
                    Solo 2 minutos
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Social proof + Avatar carousel */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="mt-10 w-full max-w-md" data-id="element-71">
              <p className="text-sm text-offwhite/90 font-bold mb-3" data-id="element-72">
                ✅ Más de 4,223 mexicanos hicieron el test
              </p>

              {/* Auto-scrolling avatar carousel */}
              <div className="relative overflow-hidden h-12 rounded-full" data-id="element-73">
                <motion.div className="flex gap-2 absolute" animate={{
                x: [0, -(56 * 6)]
              }} transition={{
                x: {
                  repeat: Infinity,
                  repeatType: 'loop',
                  duration: 12,
                  ease: 'linear'
                }
              }} data-id="element-74">
                  {[...visibleAvatars, ...visibleAvatars].map((src, i) => <div key={i} className="w-12 h-12 rounded-full border-2 border-navy bg-cream overflow-hidden flex-shrink-0" data-id="element-75">
                      <img src={src} alt="Persona que hizo el test" className="w-full h-full object-cover" data-id="element-76" />
                    </div>)}
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Right Decoration */}
          <div className="lg:col-span-5 hidden lg:flex justify-center relative" data-id="element-77">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1
          }} className="relative w-full aspect-square max-w-md flex items-center justify-center" data-id="element-78">
              {/* Super minimalist head with anxiety dot */}
              <svg viewBox="0 0 200 200" className="w-2/3 h-2/3 drop-shadow-2xl" data-id="element-79">
                {/* Minimalist Head Outline */}
                <path d="M100 20 C60 20 40 50 40 90 C40 120 55 140 70 160 L70 190 L130 190 L130 160 C145 140 160 120 160 90 C160 50 140 20 100 20 Z" fill="none" stroke="#F0EBE3" strokeWidth="3" strokeOpacity="0.4" data-id="element-80" />

                {/* Blinking Red Dot (Ansiedad) */}
                <circle cx="100" cy="80" r="8" fill="#DC2626" className="animate-pulse" data-id="element-81" />
                <circle cx="100" cy="80" r="16" fill="#DC2626" fillOpacity="0.3" className="animate-pulse-slow" data-id="element-82" />
                <circle cx="100" cy="80" r="24" fill="#DC2626" fillOpacity="0.1" className="animate-ping" data-id="element-83" />

                {/* Label */}
                <text x="125" y="85" fill="#DC2626" fontSize="12" fontWeight="bold" data-id="element-84">
                  Ansiedad
                </text>
              </svg>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}